import React, { useEffect, useState, useMemo } from "react";
import {
  Container,
  Card,
  Spinner,
  Badge,
  Button,
  Form,
  Alert,
} from "react-bootstrap";
import { motion } from "framer-motion";
import { Storage } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import "./HealthTimelinePage.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faTrash,faFileLines, } from "@fortawesome/free-solid-svg-icons";
import PetPageHeader from "../../components/PetPageHeader";
import FloatingPawButton from "../../components/FloatingPawButton";

// ⚠️ Reuse the same URL you use in SmartTagSetup
const PET_HEALTH_LAMBDA_URL =
  "https://rpjj6asb422e7dke7qx4hkouf40shzpk.lambda-url.us-east-1.on.aws/";

// ------- Date formatting (same behavior as SmartTagSetup) -------
function formatDateTimeDisplay(raw) {
  if (!raw) return "";

  const trimmed = String(raw).trim();

  // Handle canonical formats we use in Dynamo:
  // 1) "YYYY-MM-DD"
  // 2) "YYYY-MM-DD HH:MM" or "YYYY-MM-DDTHH:MM"
  let m = trimmed.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2})(?::(\d{2}))?)?$/
  );

  let parsed;

  if (m) {
    const [, y, mStr, dStr, hStr, minStr] = m;
    const year = Number(y);
    const monthIndex = Number(mStr) - 1; // 0-based
    const day = Number(dStr);
    const hours = hStr ? Number(hStr) : 0;
    const minutes = minStr ? Number(minStr) : 0;

    parsed = new Date(year, monthIndex, day, hours, minutes); // LOCAL time
  } else {
    // Fallback: best-effort parse anything else (rare)
    parsed = new Date(trimmed);
  }

  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }

  const month = parsed.getMonth() + 1;
  const day = parsed.getDate();
  const year = parsed.getFullYear();

  let hours = parsed.getHours();
  const minutes = parsed.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  if (hours === 0) hours = 12;

  let timeStr = `${hours}`;
  if (minutes !== 0) {
    const minStr = String(minutes).padStart(2, "0");
    timeStr += `:${minStr}`;
  }
  timeStr += ampm;

  return `${month}/${day}/${year} ${timeStr}`;
}

function parseCanonicalToMillis(raw) {
  if (!raw) return 0;
  const trimmed = String(raw).trim();

  const m = trimmed.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2})(?::(\d{2}))?)?$/
  );
  if (m) {
    const [, y, mStr, dStr, hStr, minStr] = m;
    const year = Number(y);
    const monthIndex = Number(mStr) - 1;
    const day = Number(dStr);
    const hours = hStr ? Number(hStr) : 0;
    const minutes = minStr ? Number(minStr) : 0;
    return new Date(year, monthIndex, day, hours, minutes).getTime();
  }

  const fallback = new Date(trimmed).getTime();
  return Number.isNaN(fallback) ? 0 : fallback;
}

// ------- Lambda helpers (same contract as SmartTagSetup) -------
async function listHealthRecords(tagId) {
  try {
    const resp = await fetch(PET_HEALTH_LAMBDA_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "listRecords",
        tagId: tagId.toUpperCase(),
      }),
    });
    if (!resp.ok) return [];
    const data = await resp.json();
    return data.items || [];
  } catch (err) {
    console.error("listHealthRecords error:", err);
    return [];
  }
}

async function listVetReminders(tagId) {
  try {
    const resp = await fetch(PET_HEALTH_LAMBDA_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "listReminders",
        tagId: tagId.toUpperCase(),
      }),
    });
    if (!resp.ok) return [];
    const data = await resp.json();
    return data.items || [];
  } catch (err) {
    console.error("listVetReminders error:", err);
    return [];
  }
}

// ✅ helper to delete a reminder
async function deleteVetReminder(tagId, sk) {
  try {
    const resp = await fetch(PET_HEALTH_LAMBDA_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "deleteReminder",
        tagId: tagId.toUpperCase(),
        sk,
      }),
    });
    return resp.ok;
  } catch (err) {
    console.error("deleteVetReminder error:", err);
    return false;
  }
}

async function addHealthRecordUploadLog(tagId, fileName, s3Key) {
  try {
    const resp = await fetch(PET_HEALTH_LAMBDA_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "addRecord",
        tagId: tagId.toUpperCase(),
        petName: null,
        date: new Date().toISOString().slice(0, 10),
        reason: "Uploaded document",
        clinic: "",
        notes: `Document: ${fileName} (S3 key: ${s3Key})`,
      }),
    });
    return resp.ok;
  } catch (err) {
    console.error("addHealthRecordUploadLog error:", err);
    return false;
  }
}

// ✅ helper to delete a health record
async function deleteHealthRecord(tagId, sk) {
  try {
    const resp = await fetch(PET_HEALTH_LAMBDA_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "deleteRecord",
        tagId: tagId.toUpperCase(),
        sk,
      }),
    });
    return resp.ok;
  } catch (err) {
    console.error("deleteHealthRecord error:", err);
    return false;
  }
}

export default function HealthTimelinePage() {
  const navigate = useNavigate();

  const [records, setRecords] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [loadingReminders, setLoadingReminders] = useState(true);
  const [error, setError] = useState("");
  const [cancelingSk, setCancelingSk] = useState(null);

  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadMessage, setUploadMessage] = useState("");
  const [viewingDocKey, setViewingDocKey] = useState(null);
  const [deletingDocSk, setDeletingDocSk] = useState(null);
  const [deletingRecSk, setDeletingRecSk] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);


  const getDocInfoFromNotes = (notes) => {
    if (!notes) return { key: null, label: null };

    // (S3 key: health/TAG/docs/12345_file-name.pdf)
    const keyMatch = notes.match(/\(S3 key:\s*([^)]+)\)/i);
    const key = keyMatch ? keyMatch[1].trim() : null;

    // Optional: "Uploaded document: Vaccination Record.pdf"
    const labelMatch = notes.match(/Uploaded document:\s*([^()]+)/i);
    let label = labelMatch ? labelMatch[1].trim() : null;

    // Derive from key if no label
    if (!label && key) {
      const filePart = key.split("/").pop() || key;
      // Strip timestamp like 1700766342345_ and make it nice
      label = filePart.replace(/^\d+[_-]/, "").replace(/_/g, " ");
    }

    return { key, label };
  };

  const handleViewDocument = async (rec) => {
    const { key } = getDocInfoFromNotes(rec.notes);
    if (!key) {
      alert("We couldn't find a document link for this record.");
      return;
    }

    try {
      setViewingDocKey(key);
      const url = await Storage.get(key, { level: "private" });
      window.open(url, "_blank", "noopener");
    } catch (err) {
      console.error("handleViewDocument error:", err);
      alert("We couldn't open that document. Please try again.");
    } finally {
      setViewingDocKey(null);
    }
  };

  const handleDeleteDocument = async (rec) => {
    const { key } = getDocInfoFromNotes(rec.notes);
    if (!key) {
      alert("We couldn't find a document key to delete.");
      return;
    }

    const confirmed = window.confirm(
      "Delete this document and remove it from your health history? This cannot be undone."
    );
    if (!confirmed) return;

    try {
      setDeletingDocSk(rec.sk || null);

      // 1) Delete from S3
      await Storage.remove(key, { level: "private" });

      // 2) Delete health record entry via Lambda
      const ok = await deleteHealthRecord(tagId, rec.sk);
      if (!ok) {
        alert("We deleted the file, but could not remove the health record row.");
      }

      // 3) Update local state
      setRecords((prev) =>
        prev.filter((r) => !(r.pk === rec.pk && r.sk === rec.sk))
      );
    } catch (err) {
      console.error("handleDeleteDocument error:", err);
      alert("We couldn't delete that document. Please try again.");
    } finally {
      setDeletingDocSk(null);
    }
  };

  // Pull SmartTag from localStorage (same as rest of app)
  const tagId = useMemo(
    () => (localStorage.getItem("lpp.tagId") || "").toUpperCase(),
    []
  );

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!tagId) {
      setUploadError("Link a SmartTag first to upload health documents.");
      return;
    }
    if (!uploadFile) {
      setUploadError("Please choose a file to upload.");
      return;
    }

    setUploading(true);
    setUploadError("");
    setUploadMessage("");

    try {
      const timestamp = Date.now();
      const key = `health/${tagId}/docs/${timestamp}_${uploadFile.name}`;

      await Storage.put(key, uploadFile, {
        level: "private",
        contentType: uploadFile.type || "application/octet-stream",
      });

      // Log this as a health record so it shows in the timeline
      await addHealthRecordUploadLog(tagId, uploadFile.name, key);

    setUploadMessage("Document uploaded and linked to your health history.");
    setTimeout(() => setUploadMessage(""), 3000);

setUploadFile(null);
setFileInputKey((k) => k + 1);
await refreshPageData();

// Reload records so the new “Uploaded document” appears
try {
  setLoadingRecords(true);
  const rec = await listHealthRecords(tagId);
  setRecords(rec || []);
} finally {
  setLoadingRecords(false);
}




    } catch (err) {
      console.error("handleUploadDocument error:", err);
      setUploadError("We couldn’t upload that document. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (!tagId) {
      setLoadingRecords(false);
      setLoadingReminders(false);
      return;
    }

    const load = async () => {
      try {
        setLoadingRecords(true);
        setLoadingReminders(true);

        const [rec, rem] = await Promise.all([
          listHealthRecords(tagId),
          listVetReminders(tagId),
        ]);

        setRecords(rec || []);
        setReminders(rem || []);
      } catch (err) {
        console.error("Health timeline load error:", err);
        setError("We had trouble loading your health timeline.");
      } finally {
        setLoadingRecords(false);
        setLoadingReminders(false);
      }
    };

    load();
  }, [tagId]);

  // 🔹 Show ALL reminders in this page section (soonest first)
const sortedReminders = useMemo(() => {
  if (!reminders || reminders.length === 0) return [];

  const now = Date.now();

  const filtered = reminders.filter((r) => {
    const raw =
      r.dateTime ||
      (r.sk && r.sk.startsWith("REM#") ? r.sk.replace("REM#", "") : "");
    const ms = parseCanonicalToMillis(raw);
    return ms === 0 || ms >= now - 60 * 1000; // allow 1 min grace
  });

  filtered.sort((a, b) => {
    const aRaw =
      a.dateTime ||
      (a.sk && a.sk.startsWith("REM#") ? a.sk.replace("REM#", "") : "");
    const bRaw =
      b.dateTime ||
      (b.sk && b.sk.startsWith("REM#") ? b.sk.replace("REM#", "") : "");
    return parseCanonicalToMillis(aRaw) - parseCanonicalToMillis(bRaw);
  });

  return filtered;
}, [reminders]);

  // 🔹 Health history uses only records (REC#) – newest first
  const sortedRecords = useMemo(() => {
    if (!records) return [];
    const cp = [...records];
    cp.sort((a, b) => {
      const aRaw =
        a.date ||
        (a.sk && a.sk.startsWith("REC#") ? a.sk.replace("REC#", "") : "");
      const bRaw =
        b.date ||
        (b.sk && b.sk.startsWith("REC#") ? b.sk.replace("REC#", "") : "");
      return parseCanonicalToMillis(bRaw) - parseCanonicalToMillis(aRaw); // newest first
    });
    return cp;
  }, [records]);

  // ✅ Handler for canceling a reminder from the timeline
  const refreshPageData = async () => {
  if (!tagId) return;

  try {
    setLoadingRecords(true);
    setLoadingReminders(true);

    const [rec, rem] = await Promise.all([
      listHealthRecords(tagId),
      listVetReminders(tagId),
    ]);

    setRecords(rec || []);
    setReminders(rem || []);
  } catch (e) {
    console.error("refreshPageData error:", e);
  } finally {
    setLoadingRecords(false);
    setLoadingReminders(false);
  }
};

  const handleCancelReminder = async (rem) => {
    if (!rem.sk || !tagId) return;

    const confirmed = window.confirm(
      "Cancel this vet reminder? This cannot be undone."
    );
    if (!confirmed) return;

    try {
      setCancelingSk(rem.sk);
      const ok = await deleteVetReminder(tagId, rem.sk);
      if (!ok) {
        alert("Could not cancel that reminder. Please try again.");
        return;
      }

      // Remove from local state so UI updates immediately
      setReminders((prev) => prev.filter((r) => r.sk !== rem.sk));
    } catch (err) {
      console.error("Failed to cancel reminder:", err);
      alert("Could not cancel that reminder. Please try again.");
    } finally {
      setCancelingSk(null);
    }
  };

  const extractS3KeyFromNotes = (notes) => {
    if (!notes) return null;
    // matches: Document: filename (S3 key: health/TAG/docs/123_file.pdf)
    const match = notes.match(/\(S3 key:\s*([^)]+)\)/i);
    return match ? match[1].trim() : null;
  };

  // ✅ NEW: delete a non-document health record entry
  const handleDeleteRecordEntry = async (rec) => {
    if (!tagId || !rec.sk) return;

    const confirmed = window.confirm(
      "Delete this health record from your history? This cannot be undone."
    );
    if (!confirmed) return;

    try {
      setDeletingRecSk(rec.sk);

      const ok = await deleteHealthRecord(tagId, rec.sk);
      if (!ok) {
        alert("We couldn't delete that record. Please try again.");
        return;
      }

      setRecords((prev) =>
        prev.filter((r) => !(r.pk === rec.pk && r.sk === rec.sk))
      );
    } catch (err) {
      console.error("Failed to delete health record:", err);
      alert("We couldn't delete that record. Please try again.");
    } finally {
      setDeletingRecSk(null);
    }
  };

 const handleOpenProfile = () => {

    navigate("/profile");

};



  return (
    <div className="health-timeline-wrapper">
      <Container className="health-page-container">

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="mx-auto"
          style={{ maxWidth: "720px" }}
        >
          <div>
  {/* Header */}
<PetPageHeader
  baseTitle="Health Timeline"
  subtitle="Vet visits, reminders & documents managed by your SmartTag AI."
/>




  {!tagId && (
    <p className="small text-muted mt-3 mb-0">
      Link a SmartTag to your account to start tracking health history.
    </p>
  )}

  {tagId && (
    <>
      {/* 🆕 Upload health document – panel, no nested Card */}
      <div className="mt-4">
        <div className="health-section-label mb-1">
          Upload health document
        </div>

        <div className="health-upload-panel">
          <p className="small text-muted mb-2">
            Upload vet visit summaries, lab results, or vaccination records.
            We’ll store them securely and note the upload in your health
            history.
          </p>

          {uploadError && (
            <Alert variant="danger" className="py-2 small mb-2">
              {uploadError}
            </Alert>
          )}
          {uploadMessage && (
            <Alert variant="success" className="py-2 small mb-2">
              {uploadMessage}
            </Alert>
          )}

          <Form
            onSubmit={handleUploadDocument}
            className="d-flex flex-wrap gap-2 align-items-center"
          >

<Form.Control
  key={fileInputKey}
  type="file"
  size="sm"
  onChange={(e) =>
    setUploadFile(
      e.target.files && e.target.files[0] ? e.target.files[0] : null
    )
  }
  style={{ maxWidth: 280 }}
/>


<Button
  type="submit"
  size="sm"
  disabled={uploading}
  className="lpp-pill-btn"
>
  {uploading ? "Uploading…" : "Upload"}
</Button>

          </Form>
        </div>
      </div>

      {/* 🔹 All upcoming reminders */}
      <div className="mt-4 mb-4">
        <div className="health-section-label mb-1">
          Upcoming Reminders
        </div>
        {loadingReminders ? (
          <div className="d-flex align-items-center">
            <Spinner animation="border" size="sm" className="me-2" />
            <span className="small text-muted">
              Checking reminders…
            </span>
          </div>
        ) : sortedReminders.length === 0 ? (
          <p className="small text-muted mb-0">
            No upcoming vet reminders yet. In your SmartTag chat, you can say{" "}
            <strong>“set a vet reminder”</strong> to add one.
          </p>
        ) : (
          <div className="small">
            {sortedReminders.map((rem) => {
              const rawWhen =
                rem.dateTime ||
                (rem.sk && rem.sk.startsWith("REM#")
                  ? rem.sk.replace("REM#", "")
                  : null);
              const pretty = rawWhen
                ? formatDateTimeDisplay(rawWhen)
                : "unknown time";

              return (
                <div
                  key={`${rem.pk}-${rem.sk}`}
                  className="mb-3 p-2 rounded border bg-light-subtle"
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="fw-semibold">{rem.reason}</div>
                      <div>{pretty}</div>
                      <div className="text-muted">
                        {rem.clinic || "No clinic saved"}
                      </div>
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      disabled={cancelingSk === rem.sk}
                      onClick={() => handleCancelReminder(rem)}
                    >
                      {cancelingSk === rem.sk ? "Canceling..." : "Cancel"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <hr className="my-3" />

      {/* 🔹 Health Timeline – records only */}
      {/* Quick actions → send user back to SmartTag profile */}
<div className="health-actions-row mt-4">
  <Button
    variant="outline-secondary"
    size="sm"
    onClick={handleOpenProfile}
    className="lpp-pill-btn"
    title="Open SmartTag Profile"
  >
    <FontAwesomeIcon icon={faUser} className="me-2" />

  </Button>
</div>
      <div className="d-flex justify-content-between align-items-center mb-3">


        <div className="health-section-label mb-0">Health history</div>
        <div className="small text-muted">
          Powered by your SmartTag AI 🐾
        </div>
      </div>
           {/* Quick actions → send user back to SmartTag chat */}





      {loadingRecords ? (
        <div className="d-flex align-items-center">
          <Spinner animation="border" size="sm" className="me-2" />
          <span className="small text-muted">
            Loading health records…
          </span>
        </div>
      ) : sortedRecords.length === 0 ? (
        <p className="small text-muted mb-0">
          No health visits logged yet. In your SmartTag chat, you can say{" "}
          <strong>“add record”</strong> to store a past visit.
        </p>
      ) : (
        <div className="health-timeline mt-2">
          {/* Vertical line */}
          <div className="timeline-line" />

          <div className="timeline-items">
            {sortedRecords.map((rec) => {
              const rawDate =
                rec.date ||
                (rec.sk && rec.sk.startsWith("REC#")
                  ? rec.sk.replace("REC#", "")
                  : null);
              const pretty = rawDate
                ? formatDateTimeDisplay(rawDate)
                : "unknown date";

              const isUploadDoc = rec.reason === "Uploaded document";

              return (
                <div
                  key={`${rec.pk}-${rec.sk}`}
                  className="timeline-item"
                >
                  <div className="timeline-dot" />
                  <div className="timeline-content">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <div className="timeline-date">{pretty}</div>
                        <div className="timeline-title">
                          {rec.reason}
                        </div>
                        <div className="timeline-clinic">
                          {rec.clinic || "No clinic saved"}
                        </div>

                        {/* Show notes for regular (non-document) records */}
                        {!isUploadDoc && rec.notes && (
                          <div className="timeline-notes">
                            {rec.notes}
                          </div>
                        )}
                      </div>

                      {/* Delete for non-document health records */}
                      {!isUploadDoc && (
                       <button
                    className="icon-only-btn danger"
                    disabled={deletingRecSk === rec.sk}
                    onClick={() => handleDeleteRecordEntry(rec)}
                    title="Delete record"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>

                      )}
                    </div>

                    {/* Special UI for uploaded documents */}
                    {isUploadDoc &&
                      (() => {
                        const { key, label } = getDocInfoFromNotes(
                          rec.notes
                        );
                        if (!key) return null;
                        const isThisOpening = viewingDocKey === key;

                        return (
                          <div className="mt-2 d-flex flex-wrap align-items-center gap-2">
                            <span className="small text-muted">
                              Attached document:
                            </span>
                            <div className="doc-action-group">
                                  {/* View document */}
                                  <button
                                    className="doc-icon-btn"
                                    onClick={() => handleViewDocument(rec)}
                                    disabled={isThisOpening}
                                    title="View document"
                                  >
                                    <FontAwesomeIcon icon={faFileLines} />
                                  </button>

                                  {/* Delete document */}
                                  <button
                                    className="doc-icon-btn danger"
                                    onClick={() => handleDeleteDocument(rec)}
                                    disabled={deletingDocSk === rec.sk}
                                    title="Delete document"
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>

                                  {/* Document name */}
                                  <div className="doc-label">
                                    {label || "Document"}
                                  </div>
                                </div>

                          </div>
                        );
                      })()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {error && (
        <p className="text-danger small mt-3 mb-0">{error}</p>
      )}


    </>
  )}
</div>
        </motion.div>
         <div >
          <FloatingPawButton isAuthenticated={true} hasActiveSub={true} />
        </div>
      </Container>
    </div>
  );
}
